export const PRODUCT_TYPES = [
  { key: 1, label: 'Chemical', short: 'c' },
  { key: 2, label: 'Reagent', short: 'r' },
  { key: 3, label: 'Equipment', short: 'e' },
];

/** Fallback destinations matching proasysinc delivery_collection static list */
export const STATIC_DELIVERY_OPTIONS = [
  { label: 'Office', value: '950' },
  { label: 'Storage Unit - KOP', value: '3709' },
  { label: 'Storage Unit - Philly', value: '951' },
  { label: 'Storage Unit - York', value: '3389' },
  { label: 'Storage Unit - East Penn (Lehigh Valley)', value: '952' },
  { label: 'Storage Unit - Jersey (New)', value: '6214' },
  { label: 'Philly City Delivery', value: '3869' },
  { label: 'Other', value: '' },
];

export function parseOptionList(str) {
  if (!str) return [];
  return str.replace(/[\[\]]/g, '').split(',').map((s) => s.trim()).filter(Boolean);
}

export function defaultSize(product) {
  const sizes = parseOptionList(product?.size_options);
  if (!sizes.length) return null;
  return sizes.includes('5') ? '5' : sizes[sizes.length - 1];
}

export function defaultConcentration(product) {
  const opts = parseOptionList(product?.concentration);
  return opts.length ? opts[opts.length - 1] : null;
}

export function cartLineKey(line) {
  return `${line.item.id}-${line.option?.id || ''}-${line.size || ''}-${line.concentration || ''}`;
}

export function formatQuantity(line) {
  const p = line.item;
  const qty = line.quantity;
  const size = line.size;
  const unit = p.size_unit || '';
  if (['T', 'Bulk'].includes(size)) return `${qty} ${unit} ${size}`;
  if (size === '2-W T') return `${qty}x 2-Way Tote`;
  if (size) return `${qty}x ${size}${unit}`;
  return `${qty}x`;
}

export function cartLineDetails(line) {
  const parts = [];
  if (line.option?.code) parts.push(line.option.code);
  if (line.concentration) parts.push(`${line.concentration}%`);
  return parts.join(' · ');
}

export function cartTotals(cart) {
  const lines = cart.length;
  const items = cart.reduce((sum, line) => sum + line.quantity, 0);
  return { lines, items };
}

export function recipientLabel(config, user) {
  if (config.recipientType === 'customer' && config.accountSelected?.name) {
    return config.accountSelected.name;
  }
  if (config.recipientType === 'personal') {
    return user?.employee?.full_name || user?.username || 'Personal';
  }
  return 'Select recipient';
}

export function orderRecipientName(order) {
  if (order?.customer?.name) return order.customer.name;
  if (order?.employee?.full_name) return order.employee.full_name;
  return 'Order';
}

export function formatOrderWhen(iso, { withYear = false } = {}) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      ...(withYear ? { year: 'numeric' } : {}),
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

export function orderFulfillmentLabel(order) {
  if (!order?.delivery) {
    const date = formatPickupDateForInput(order?.pickup_date);
    return date ? `Pickup · ${date}` : 'Pickup';
  }
  if (order.address?.description) return `Delivery · ${order.address.description}`;
  const match = STATIC_DELIVERY_OPTIONS.find(
    (o) => o.value !== '' && String(o.value) === String(order.address_id)
  );
  if (match) return `Delivery · ${match.label}`;
  if (order.address_id) return 'Delivery';
  return 'Delivery · See notes';
}

export function formatOrderDate(value) {
  if (!value) return '';
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[2]}/${m[3]}/${m[1]}`;
  return formatPickupDateForInput(value) || String(value);
}

export function yesNo(value) {
  return value ? 'Yes' : 'No';
}

/** Convert API date (YYYY-MM-DD…) to MM/DD/YYYY for the pickup field */
export function formatPickupDateForInput(value) {
  if (!value) return '';
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[2]}/${m[3]}/${m[1]}`;
  return '';
}

/** Map a loaded order (draft or submitted detail) into wizard config + cart */
export function orderToWizardState(order) {
  if (!order) return null;

  const lines = Array.isArray(order.line_items) ? order.line_items : [];
  const firstProduct = lines.find((li) => li?.product)?.product;
  const category = firstProduct?.category ?? firstProduct?.Category ?? null;

  const config = {
    productType: category != null ? Number(category) : null,
    recipientType: order.customer ? 'customer' : 'personal',
    accountSelected: order.customer
      ? { id: order.customer.id, name: order.customer.name }
      : null,
    method: order.delivery ? 'Delivery' : 'Pickup',
    destination: order.address_id != null && order.address_id !== ''
      ? String(order.address_id)
      : '',
    pickupDate: formatPickupDateForInput(order.pickup_date),
    charge: !!order.charge,
    notes: order.notes || '',
  };

  const cart = lines
    .filter((li) => li?.product?.id)
    .map((li) => {
      const product = { ...li.product };
      if (!Array.isArray(product.variants)) product.variants = [];
      const variant = li.product_variant;
      return {
        key: `draft-${li.id || `${product.id}-${Date.now()}`}`,
        item: product,
        option: variant
          ? { id: variant.id, code: variant.code, desc: variant.desc || variant.description }
          : null,
        size: li.size || null,
        concentration: li.concentration || null,
        quantity: li.quantity || 1,
      };
    });

  return { draftId: order.submitted ? null : order.id, config, cart };
}
